import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, defer, map, tap } from "rxjs";
import { environment } from "src/environments/environment";
import { resolveSaqFormDictionary } from "../dictionaries/saq/saq_dictionary_registry";
import evidenceRequirementSource from "../evidence_requirements/evidence_requirements.json";
import { resolveRocEvidenceRequirementTargets } from "../utils/roc_reduced_scope.util";
import { isNullableCalendarDate } from "../utils/assessment_task_date.util";
import { EvidenceRequirementInvalidationService } from "./evidence_requirement_invalidation.service";
import type {
    EvidenceRequirementChange, EvidenceRequirementChangeScope
} from "./evidence_requirement_invalidation.service";

export interface EvidenceRequirementSourceRow {
    requirementReferences: string[];
    documentName: string;
    requiredForAllAssessments: boolean;
}

export interface EvidenceDocumentRequirement {
    documentName: string;
    requiredForAllAssessments: boolean;
    matchedRequirementReferences: string[];
}

export interface EvidenceAssessmentScope {
    section: "section_2b" | "section_7";
    requirementTargets: string[];
}

export interface EvidenceAssessmentInventory extends EvidenceAssessmentScope {
    documents: EvidenceDocumentRequirement[];
}

export interface EvidenceRecipientSummaryRecipient {
    userId: number;
    displayName: string;
    role: string;
    selectedItemCount: number;
}

export interface EvidenceRecipientSummary {
    selectedItemCount: number;
    recipients: EvidenceRecipientSummaryRecipient[];
}

export interface EvidenceItemUploadContext {
    productLicenseId: number;
    merchantId: number;
    assessmentName: string;
    evidenceRequirement: {
        id: number;
        evidenceName: string;
        status: EvidenceRequirementStatus;
        dueDate: string | null;
        linkedSecureStorageFileId: number | null;
        linkedFileName: string | null;
        linkedFileAccessible: boolean;
    };
}

// Assignment controls use this server-authoritative list rather than the
// narrower email-recipient list. It intentionally contains no email address
// or merchant/storage metadata.
export interface EvidenceUploadUserOption {
    userId: number;
    displayName: string;
    email?: string;
    role: string;
}

export interface EvidenceSubmissionState {
    submittedEvidence: string[];
    canEdit: boolean;
}

export type EvidenceRequirementStatus =
    | "not_submitted"
    | "file_pending_review"
    | "file_rejected"
    | "submitted_reviewed";

export interface PersistedEvidenceRequirement {
    canEditAssigned?: boolean;
    id: number;
    evidenceName: string;
    status: EvidenceRequirementStatus;
    dueDate: string | null;
    linkedSecureStorageFileId: number | null;
    linkedFileName?: string | null;
    linkedFileAccessible?: boolean;
    requirementKeys: string[];
    assignedUserIds: number[];
    createdAt: string | null;
    updatedAt: string | null;
}

export interface EvidenceSecureStorageFileOption {
    id: number;
    fileName: string;
}

export interface PersistedEvidenceRequirementsState {
    items: PersistedEvidenceRequirement[];
    canManage: boolean;
}

export type EvidenceRequirementStatusCounts = Record<EvidenceRequirementStatus, number>;

export function countPersistedEvidenceRequirementsByStatus(
    items: readonly Pick<PersistedEvidenceRequirement, "status">[]
): EvidenceRequirementStatusCounts {
    const counts: EvidenceRequirementStatusCounts = {
        not_submitted: 0,
        file_pending_review: 0,
        submitted_reviewed: 0,
        file_rejected: 0
    };

    for (const item of items) {
        const status = item?.status;
        if (status && Object.prototype.hasOwnProperty.call(counts, status)) {
            counts[status] += 1;
        }
    }

    return counts;
}

interface EvidenceRequirementSelectionPayload {
    requirementKeys?: readonly string[];
    allRequirements?: true;
    assignedUserIds?: readonly number[];
    unassigned?: true;
}

export interface CreateEvidenceRequirementPayload extends EvidenceRequirementSelectionPayload {
    evidenceName: string;
    status?: EvidenceRequirementStatus;
    dueDate?: string | null;
    linkedSecureStorageFileId?: number | null;
}

export interface UpdateEvidenceRequirementPayload extends EvidenceRequirementSelectionPayload {
    evidenceName?: string;
    status?: EvidenceRequirementStatus;
    dueDate?: string | null;
    linkedSecureStorageFileId?: number | null;
}

export interface BulkUpdateEvidenceRequirementsPayload extends EvidenceRequirementSelectionPayload {
    evidenceRequirementIds: readonly number[];
    evidenceName?: string;
    status?: EvidenceRequirementStatus;
    dueDate?: string | null;
}

export interface EvidenceRequirementRegenerationPreview {
    currentItemCount: number;
    defaultItemCount: number;
    defaultRequirementKeyCount: number;
}

export interface EvidenceRequirementRegenerationResult {
    removedItemCount: number;
    createdItemCount: number;
    defaultItemCount: number;
    defaultRequirementKeyCount: number;
}

interface EvidenceApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
}

function requireApiData<T>(response: EvidenceApiResponse<T>, fallbackMessage: string): T {
    if (!response.success || response.data === undefined) {
        throw new Error(response.message || fallbackMessage);
    }

    return response.data;
}

function normalizeEvidenceUserOptions(value: unknown): EvidenceUploadUserOption[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map((user) => {
            const record = user && typeof user === "object" && !Array.isArray(user)
                ? user as Record<string, unknown>
                : {};
            const displayName = record["displayName"];
            const email = record["email"];
            const role = record["role"];
            return {
                userId: Number(record["userId"]),
                displayName: typeof displayName === "string" ? displayName.trim() : "",
                email: typeof email === "string" ? email.trim() : undefined,
                role: typeof role === "string" ? role.trim() : ""
            };
        })
        .filter((user) => (
            Number.isInteger(user.userId) && user.userId > 0 && user.displayName.length > 0 && user.role.length > 0
        ));
}

function normalizePositiveInteger(value: unknown): number | null {
    const normalized = Number(value);
    return Number.isInteger(normalized) && normalized > 0 ? normalized : null;
}

function normalizeNonNegativeInteger(value: unknown): number | null {
    const normalized = Number(value);
    return Number.isInteger(normalized) && normalized >= 0 ? normalized : null;
}

function normalizeEvidenceRequirementStatus(value: unknown): EvidenceRequirementStatus | null {
    switch (value) {
        case "not_submitted":
        case "file_pending_review":
        case "file_rejected":
        case "submitted_reviewed":
            return value;
        default:
            return null;
    }
}

function normalizeStringList(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return [...new Set(value
        .filter((entry): entry is string => typeof entry === "string")
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0))];
}

function normalizeUserIdList(value: unknown): number[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return [...new Set(value.map(normalizePositiveInteger).filter((userId): userId is number => userId !== null))];
}

function requireEvidenceRecipientSummary(value: unknown, fallbackMessage: string): EvidenceRecipientSummary {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new Error(fallbackMessage);
    }

    const record = value as Record<string, unknown>;
    const selectedItemCount = normalizeNonNegativeInteger(record["selectedItemCount"]);
    if (selectedItemCount === null || !Array.isArray(record["recipients"])) {
        throw new Error(fallbackMessage);
    }

    const recipients = record["recipients"].map((value): EvidenceRecipientSummaryRecipient | null => {
        if (!value || typeof value !== "object" || Array.isArray(value)) {
            return null;
        }

        const recipient = value as Record<string, unknown>;
        const userId = normalizePositiveInteger(recipient["userId"]);
        const recipientItemCount = normalizeNonNegativeInteger(recipient["selectedItemCount"]);
        const displayName = typeof recipient["displayName"] === "string" ? recipient["displayName"].trim() : "";
        const role = typeof recipient["role"] === "string" ? recipient["role"].trim() : "";
        if (!userId || recipientItemCount === null || recipientItemCount === 0 || !displayName || !role) {
            return null;
        }

        return { userId, displayName, role, selectedItemCount: recipientItemCount };
    });
    if (recipients.some((recipient) => recipient === null)) {
        throw new Error(fallbackMessage);
    }

    return {
        selectedItemCount,
        recipients: recipients as EvidenceRecipientSummaryRecipient[]
    };
}

function requireEvidenceItemUploadContext(value: unknown, fallbackMessage: string): EvidenceItemUploadContext {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new Error(fallbackMessage);
    }

    const record = value as Record<string, unknown>;
    const productLicenseId = normalizePositiveInteger(record["productLicenseId"]);
    const merchantId = normalizePositiveInteger(record["merchantId"]);
    const assessmentName = typeof record["assessmentName"] === "string" ? record["assessmentName"].trim() : "";
    const evidenceRequirement = record["evidenceRequirement"];
    if (!evidenceRequirement || typeof evidenceRequirement !== "object" || Array.isArray(evidenceRequirement)) {
        throw new Error(fallbackMessage);
    }

    const item = evidenceRequirement as Record<string, unknown>;
    const evidenceRequirementId = normalizePositiveInteger(item["id"]);
    const evidenceName = typeof item["evidenceName"] === "string" ? item["evidenceName"].trim() : "";
    const status = normalizeEvidenceRequirementStatus(item["status"]);
    const dueDate = item["dueDate"];
    if (!productLicenseId || !merchantId || !assessmentName || !evidenceRequirementId || !evidenceName || !status ||
        !isNullableCalendarDate(dueDate)) {
        throw new Error(fallbackMessage);
    }

    return {
        productLicenseId,
        merchantId,
        assessmentName,
        evidenceRequirement: {
            id: evidenceRequirementId,
            evidenceName,
            status,
            dueDate,
            linkedSecureStorageFileId: normalizePositiveInteger(item["linkedSecureStorageFileId"]),
            linkedFileName: typeof item["linkedFileName"] === "string" ? item["linkedFileName"].trim() || null : null,
            linkedFileAccessible: item["linkedFileAccessible"] === true
        }
    };
}

function normalizePersistedEvidenceRequirement(value: unknown): PersistedEvidenceRequirement | null {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return null;
    }

    const record = value as Record<string, unknown>;
    const id = normalizePositiveInteger(record["id"]);
    const evidenceName = typeof record["evidenceName"] === "string" ? record["evidenceName"].trim() : "";
    const status = normalizeEvidenceRequirementStatus(record["status"]);
    const dueDate = record["dueDate"];
    if (!id || !evidenceName || !status || !isNullableCalendarDate(dueDate)) {
        return null;
    }

    return {
        id,
        evidenceName,
        status,
        dueDate,
        linkedSecureStorageFileId: normalizePositiveInteger(record["linkedSecureStorageFileId"]),
        linkedFileName: typeof record["linkedFileName"] === "string" ? record["linkedFileName"].trim() || null : null,
        linkedFileAccessible: record["linkedFileAccessible"] === true,
        requirementKeys: normalizeStringList(record["requirementKeys"]),
        assignedUserIds: normalizeUserIdList(record["assignedUserIds"]),
        createdAt: typeof record["createdAt"] === "string" ? record["createdAt"] : null,
        updatedAt: typeof record["updatedAt"] === "string" ? record["updatedAt"] : null,
        canEditAssigned: record["canEditAssigned"] === true
    };
}

function requirePersistedEvidenceRequirement(value: unknown, fallbackMessage: string): PersistedEvidenceRequirement {
    const item = normalizePersistedEvidenceRequirement(value);
    if (!item) {
        throw new Error(fallbackMessage);
    }

    return item;
}

function requirePersistedEvidenceRequirements(value: unknown, fallbackMessage: string): PersistedEvidenceRequirement[] {
    if (!Array.isArray(value)) {
        throw new Error(fallbackMessage);
    }

    return value.map((item) => requirePersistedEvidenceRequirement(item, fallbackMessage));
}

function evidenceChangeScope(item: PersistedEvidenceRequirement): EvidenceRequirementChangeScope {
    return {
        requirementKeys: [...item.requirementKeys],
        assignedUserIds: [...item.assignedUserIds],
        status: item.status
    };
}

function requireEvidenceRequirementRegenerationPreview(
    value: unknown,
    fallbackMessage: string
): EvidenceRequirementRegenerationPreview {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new Error(fallbackMessage);
    }

    const record = value as Record<string, unknown>;
    const currentItemCount = normalizeNonNegativeInteger(record["currentItemCount"]);
    const defaultItemCount = normalizeNonNegativeInteger(record["defaultItemCount"]);
    const defaultRequirementKeyCount = normalizeNonNegativeInteger(record["defaultRequirementKeyCount"]);
    if (currentItemCount === null || defaultItemCount === null || defaultRequirementKeyCount === null) {
        throw new Error(fallbackMessage);
    }

    return { currentItemCount, defaultItemCount, defaultRequirementKeyCount };
}

function requireEvidenceRequirementRegenerationResult(
    value: unknown,
    fallbackMessage: string
): EvidenceRequirementRegenerationResult {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new Error(fallbackMessage);
    }

    const record = value as Record<string, unknown>;
    const removedItemCount = normalizeNonNegativeInteger(record["removedItemCount"]);
    const createdItemCount = normalizeNonNegativeInteger(record["createdItemCount"]);
    const defaultItemCount = normalizeNonNegativeInteger(record["defaultItemCount"]);
    const defaultRequirementKeyCount = normalizeNonNegativeInteger(record["defaultRequirementKeyCount"]);
    if (
        removedItemCount === null ||
        createdItemCount === null ||
        defaultItemCount === null ||
        defaultRequirementKeyCount === null
    ) {
        throw new Error(fallbackMessage);
    }

    return { removedItemCount, createdItemCount, defaultItemCount, defaultRequirementKeyCount };
}

function buildEvidenceRequirementPayload<T extends EvidenceRequirementSelectionPayload>(payload: T): T {
    if (payload.allRequirements === true && payload.requirementKeys !== undefined) {
        throw new Error("Choose either All requirements or an explicit requirement list.");
    }
    if (payload.unassigned === true && payload.assignedUserIds !== undefined) {
        throw new Error("Choose either Unassigned or an explicit assigned-user list.");
    }

    return {
        ...payload,
        requirementKeys: payload.requirementKeys ? [...payload.requirementKeys] : undefined,
        assignedUserIds: payload.assignedUserIds ? [...payload.assignedUserIds] : undefined
    } as T;
}

const EVIDENCE_REQUIREMENT_SOURCE = evidenceRequirementSource as EvidenceRequirementSourceRow[];
const SUPPORTED_SAQ_FORM_TYPES = new Set([
    "SAQ A",
    "SAQ A (EP)",
    "SAQ B",
    "SAQ B (IP)",
    "SAQ C",
    "SAQ C (VT)",
    "SAQ D",
    "SAQ D (SP)",
    "SAQ P2PE",
    "SAQ SPoC"
]);

function normalizeRequirementTarget(value: unknown): string {
    return typeof value === "string"
        ? value.trim().toUpperCase().replace(/_/g, ".")
        : "";
}

function countDecimalSeparators(value: string): number {
    return value.split(".").length - 1;
}

function normalizeAssessmentFormType(value: unknown): string {
    return typeof value === "string"
        ? value
            .trim()
            .toUpperCase()
            .replace(/\s+/g, " ")
            .replace(/\s*\(\s*/g, " (")
            .replace(/\s*\)\s*/g, ")")
            .replace(/\s*-\s*/g, " ")
            .trim()
        : "";
}

export function matchesEvidenceRequirementReference(
    reference: unknown,
    requirementTarget: unknown
): boolean {
    const normalizedReference = normalizeRequirementTarget(reference);
    const normalizedTarget = normalizeRequirementTarget(requirementTarget);

    if (!normalizedReference || !normalizedTarget || normalizedReference === "ALL") {
        return false;
    }

    if (countDecimalSeparators(normalizedReference) === 1) {
        return (
            normalizedReference === normalizedTarget ||
            normalizedTarget.startsWith(normalizedReference + ".")
        );
    }

    return normalizedReference === normalizedTarget;
}

@Injectable({ providedIn: "root" })
export class EvidenceRequirementsService {
    private readonly apiBaseUrl = environment.baseurl.replace(/\/$/, "");

    constructor(
        private readonly http: HttpClient,
        private readonly invalidations: EvidenceRequirementInvalidationService
    ) {}

    private withEvidenceInvalidation<T>(
        request: Observable<T>,
        changeFor: (result: T) => EvidenceRequirementChange
    ): Observable<T> {
        return defer(() => {
            const publish = this.invalidations.capturePublisher();
            return request.pipe(tap((result) => publish(changeFor(result))));
        });
    }

    getEvidenceForLicense(productLicenseId: number): Observable<EvidenceDocumentRequirement[]> {
        return this.http
            .get<EvidenceApiResponse<EvidenceDocumentRequirement[]>>(
                `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}`
            )
            .pipe(
                map((response) => requireApiData(response, "Unable to load evidence requirements.")),
                map((data) => Array.isArray(data) ? data.map((row) => ({
                    documentName: typeof row.documentName === "string" ? row.documentName.trim() : "",
                    requiredForAllAssessments: row.requiredForAllAssessments === true,
                    matchedRequirementReferences: Array.isArray(row.matchedRequirementReferences)
                        ? row.matchedRequirementReferences.filter((reference): reference is string => typeof reference === "string")
                        : []
                })).filter((row) => row.documentName.length > 0) : [])
            );
    }

    getEvidenceRecipientSummary(
        productLicenseId: number,
        evidenceRequirementIds: readonly number[]
    ): Observable<EvidenceRecipientSummary> {
        return this.http
            .post<EvidenceApiResponse<unknown>>(
                `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}/recipient-summary`,
                { evidenceRequirementIds: [...evidenceRequirementIds] }
            )
            .pipe(
                map((response) => requireApiData(response, "Unable to load recipients for the selected evidence items.")),
                map((data) => requireEvidenceRecipientSummary(
                    data,
                    "Unable to load recipients for the selected evidence items."
                ))
            );
    }

    getEvidenceItemUploadContext(
        productLicenseId: number,
        merchantId: number,
        evidenceRequirementId: number
    ): Observable<EvidenceItemUploadContext> {
        return this.http
            .get<EvidenceApiResponse<unknown>>(
                `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}/items/${evidenceRequirementId}/upload-context`,
                { params: { merchantId: String(merchantId) } }
            )
            .pipe(
                map((response) => requireApiData(response, "Unable to validate the evidence-item upload link.")),
                map((data) => {
                    const context = requireEvidenceItemUploadContext(
                        data, "Unable to validate the evidence-item upload link."
                    );
                    if (context.productLicenseId !== productLicenseId || context.merchantId !== merchantId ||
                        context.evidenceRequirement.id !== evidenceRequirementId) {
                        throw new Error("The evidence-item upload link is no longer available.");
                    }
                    return context;
                })
            );
    }

    applyEvidenceItemUploadTransition(
        productLicenseId: number,
        evidenceRequirementId: number,
        merchantId: number,
        linkedSecureStorageFileId: number
    ): Observable<PersistedEvidenceRequirement> {
        const request = this.http
            .post<EvidenceApiResponse<{ item?: unknown }>>(
                `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}/items/${evidenceRequirementId}/upload-transition`,
                { merchantId, linkedSecureStorageFileId }
            )
            .pipe(
                map((response) => requireApiData(response, "Unable to link the uploaded file to the evidence item.")),
                map((data) => requirePersistedEvidenceRequirement(
                    data?.item,
                    "Unable to link the uploaded file to the evidence item."
                ))
            );
        return this.withEvidenceInvalidation(request, (item) => ({
            kind: "upload",
            productLicenseId,
            dueDateMayHaveChanged: false,
            unfinishedCountMayHaveChanged: true,
            // Storage Only responses intentionally mask assignment and mapping data.
            items: item.id === evidenceRequirementId ? [{
                id: item.id,
                before: null,
                after: { requirementKeys: null, assignedUserIds: null, status: item.status }
            }] : null
        }));
    }

    getEvidenceUploadUsers(productLicenseId: number): Observable<EvidenceUploadUserOption[]> {
        return this.http
            .get<EvidenceApiResponse<{ users?: unknown }>>(
                `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}/upload-users`
            )
            .pipe(
                map((response) => requireApiData(response, "Unable to load eligible evidence upload users.")),
                map((data) => normalizeEvidenceUserOptions(data?.users))
            );
    }

    getEvidenceRequirementEditorOptions(productLicenseId: number): Observable<string[]> {
        return this.http
            .get<EvidenceApiResponse<{ requirementKeys?: unknown }>>(
                `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}/items/editor-options`
            )
            .pipe(
                map((response) => requireApiData(response, "Unable to load current in-scope requirements.")),
                map((data) => Array.isArray(data?.requirementKeys)
                    ? [...new Set(data.requirementKeys
                        .filter((requirementKey): requirementKey is string => typeof requirementKey === "string")
                        .map((requirementKey) => requirementKey.trim())
                        .filter((requirementKey) => requirementKey.length > 0))]
                    : [])
            );
    }

    getEvidenceSecureStorageFileOptions(productLicenseId: number, evidenceRequirementId?: number): Observable<EvidenceSecureStorageFileOption[]> {
        return this.http
            .get<EvidenceApiResponse<{ files?: unknown }>>(
                `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}/items/secure-storage-files`,
                { params: evidenceRequirementId ? { evidenceRequirementId } : {} }
            )
            .pipe(
                map((response) => requireApiData(response, "Unable to load Secure Storage files.")),
                map((data) => Array.isArray(data?.files) ? data.files.reduce<EvidenceSecureStorageFileOption[]>((files, value) => {
                    if (!value || typeof value !== "object" || Array.isArray(value)) {
                        return files;
                    }
                    const record = value as Record<string, unknown>;
                    const id = normalizePositiveInteger(record["id"]);
                    const fileName = typeof record["fileName"] === "string" ? record["fileName"].trim() : "";
                    if (id && fileName) {
                        files.push({ id, fileName });
                    }
                    return files;
                }, []) : [])
            );
    }

    getPersistedEvidenceRequirements(productLicenseId: number): Observable<PersistedEvidenceRequirementsState> {
        return this.http
            .get<EvidenceApiResponse<{ items?: unknown; canManage?: unknown }>>(
                `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}/items`
            )
            .pipe(
                map((response) => requireApiData(response, "Unable to load persisted evidence requirements.")),
                map((data) => ({
                    items: requirePersistedEvidenceRequirements(
                        data?.items,
                        "Unable to load persisted evidence requirements."
                    ),
                    canManage: data?.canManage === true
                }))
            );
    }

    getEvidenceRequirementRegenerationPreview(
        productLicenseId: number
    ): Observable<EvidenceRequirementRegenerationPreview> {
        return this.http
            .get<EvidenceApiResponse<unknown>>(
                `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}/items/regeneration-preview`
            )
            .pipe(
                map((response) => requireApiData(response, "Unable to preview default evidence requirements.")),
                map((data) => requireEvidenceRequirementRegenerationPreview(
                    data,
                    "Unable to preview default evidence requirements."
                ))
            );
    }

    regenerateDefaultEvidenceRequirements(
        productLicenseId: number
    ): Observable<EvidenceRequirementRegenerationResult> {
        const request = this.http
            .post<EvidenceApiResponse<unknown>>(
                `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}/items/regenerate-defaults`,
                { confirm: true }
            )
            .pipe(
                map((response) => requireApiData(response, "Unable to regenerate default evidence requirements.")),
                map((data) => requireEvidenceRequirementRegenerationResult(
                    data,
                    "Unable to regenerate default evidence requirements."
                ))
            );
        return this.withEvidenceInvalidation(request, () => ({
            kind: "regenerate", productLicenseId, dueDateMayHaveChanged: true,
            unfinishedCountMayHaveChanged: true, items: null
        }));
    }

    createPersistedEvidenceRequirement(
        productLicenseId: number,
        payload: CreateEvidenceRequirementPayload
    ): Observable<PersistedEvidenceRequirement> {
        const request = this.http
            .post<EvidenceApiResponse<{ item?: unknown }>>(
                `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}/items`,
                buildEvidenceRequirementPayload(payload)
            )
            .pipe(
                map((response) => requireApiData(response, "Unable to create the evidence requirement.")),
                map((data) => requirePersistedEvidenceRequirement(data?.item, "Unable to create the evidence requirement."))
            );
        return this.withEvidenceInvalidation(request, (item) => ({
            kind: "create", productLicenseId, dueDateMayHaveChanged: item.dueDate !== null,
            unfinishedCountMayHaveChanged: true,
            items: [{ id: item.id, before: null, after: evidenceChangeScope(item) }]
        }));
    }

    updatePersistedEvidenceRequirement(
        productLicenseId: number,
        evidenceRequirementId: number,
        payload: UpdateEvidenceRequirementPayload
    ): Observable<PersistedEvidenceRequirement> {
        const dueDateMayHaveChanged = payload.dueDate !== undefined;
        const unfinishedCountMayHaveChanged = payload.status !== undefined ||
            payload.assignedUserIds !== undefined || payload.unassigned === true ||
            payload.linkedSecureStorageFileId !== undefined;
        const request = this.http
            .put<EvidenceApiResponse<{ item?: unknown }>>(
                `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}/items/${evidenceRequirementId}`,
                buildEvidenceRequirementPayload(payload)
            )
            .pipe(
                map((response) => requireApiData(response, "Unable to update the evidence requirement.")),
                map((data) => requirePersistedEvidenceRequirement(data?.item, "Unable to update the evidence requirement."))
            );
        return this.withEvidenceInvalidation(request, (item) => ({
            kind: "update", productLicenseId, dueDateMayHaveChanged, unfinishedCountMayHaveChanged,
            items: item.id === evidenceRequirementId ? [{
                id: item.id, before: null, after: evidenceChangeScope(item)
            }] : null
        }));
    }

    deletePersistedEvidenceRequirement(
        productLicenseId: number,
        evidenceRequirementId: number
    ): Observable<PersistedEvidenceRequirement> {
        const request = this.http
            .delete<EvidenceApiResponse<{ item?: unknown }>>(
                `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}/items/${evidenceRequirementId}`
            )
            .pipe(
                map((response) => requireApiData(response, "Unable to delete the evidence requirement.")),
                map((data) => requirePersistedEvidenceRequirement(data?.item, "Unable to delete the evidence requirement."))
            );
        return this.withEvidenceInvalidation(request, (item) => ({
            kind: "delete", productLicenseId, dueDateMayHaveChanged: item.dueDate !== null,
            unfinishedCountMayHaveChanged: true,
            items: item.id === evidenceRequirementId ? [{
                id: item.id, before: evidenceChangeScope(item), after: null
            }] : null
        }));
    }

    bulkUpdatePersistedEvidenceRequirements(
        productLicenseId: number,
        payload: BulkUpdateEvidenceRequirementsPayload
    ): Observable<PersistedEvidenceRequirement[]> {
        const evidenceRequirementIds = [...payload.evidenceRequirementIds];
        const dueDateMayHaveChanged = payload.dueDate !== undefined;
        const unfinishedCountMayHaveChanged = payload.status !== undefined ||
            payload.assignedUserIds !== undefined || payload.unassigned === true;
        const request = this.http
            .patch<EvidenceApiResponse<{ items?: unknown }>>(
                `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}/items/bulk`,
                buildEvidenceRequirementPayload({ ...payload, evidenceRequirementIds })
            )
            .pipe(
                map((response) => requireApiData(response, "Unable to bulk update evidence requirements.")),
                map((data) => requirePersistedEvidenceRequirements(
                    data?.items,
                    "Unable to bulk update evidence requirements."
                ))
            );
        const requestedIds = new Set(evidenceRequirementIds);
        return this.withEvidenceInvalidation(request, (items) => ({
            kind: "bulk", productLicenseId, dueDateMayHaveChanged, unfinishedCountMayHaveChanged,
            items: items.length > 0 && items.length === requestedIds.size &&
                items.every((item) => requestedIds.has(item.id))
                ? items.map((item) => ({ id: item.id, before: null, after: evidenceChangeScope(item) }))
                : null
        }));
    }

    getSubmittedEvidence(productLicenseId: number): Observable<EvidenceSubmissionState> {
        return this.http
            .get<EvidenceApiResponse<EvidenceSubmissionState>>(
                `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}/submitted-evidence`
            )
            .pipe(
                map((response) => requireApiData(response, "Unable to load submitted evidence.")),
                map((data) => ({
                    submittedEvidence: Array.isArray(data.submittedEvidence)
                        ? data.submittedEvidence
                            .filter((name): name is string => typeof name === "string")
                            .map((name) => name.trim())
                            .filter((name) => name.length > 0)
                        : [],
                    canEdit: data.canEdit === true
                }))
            );
    }

    updateSubmittedEvidence(
        productLicenseId: number,
        submittedEvidence: readonly string[]
    ): Observable<EvidenceSubmissionState> {
        return this.http
            .put<EvidenceApiResponse<EvidenceSubmissionState>>(
                `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}/submitted-evidence`,
                { submittedEvidence: [...submittedEvidence] }
            )
            .pipe(
                map((response) => requireApiData(response, "Unable to update submitted evidence.")),
                map((data) => ({
                    submittedEvidence: Array.isArray(data.submittedEvidence)
                        ? data.submittedEvidence.filter((name): name is string => typeof name === "string")
                        : [],
                    canEdit: data.canEdit === true
                }))
            );
    }

    sendEvidenceEmail(productLicenseId: number, evidenceRequirementIds: readonly number[]): Observable<{ sent: boolean }> {
        return this.http.post<EvidenceApiResponse<{ sent: boolean }>>(
            `${this.apiBaseUrl}/evidence-requirements/${productLicenseId}/email`,
            { evidenceRequirementIds: [...evidenceRequirementIds] }
        ).pipe(
            map((response) => {
                const data = requireApiData(response, "Unable to send evidence requirements.");
                if (data.sent !== true) {
                    throw new Error("Unable to send evidence requirements.");
                }
                return { sent: true };
            })
        );
    }

    getInScopeRequirementTargets(
        formType: unknown,
        descopedSelections: unknown
    ): EvidenceAssessmentScope {
        const normalizedFormType = normalizeAssessmentFormType(formType);

        if (normalizedFormType === "ROC") {
            return {
                section: "section_7",
                requirementTargets: resolveRocEvidenceRequirementTargets(descopedSelections)
            };
        }

        if (SUPPORTED_SAQ_FORM_TYPES.has(normalizedFormType)) {
            const requirements = resolveSaqFormDictionary(normalizedFormType).section_2b?.requirements || {};
            return {
                section: "section_2b",
                requirementTargets: Object.keys(requirements)
            };
        }

        return {
            section: "section_2b",
            requirementTargets: []
        };
    }

    getDocumentsForRequirementTargets(
        inScopeRequirementTargets: readonly unknown[] | null | undefined
    ): EvidenceDocumentRequirement[] {
        const targetSet = new Set(
            (inScopeRequirementTargets ?? [])
                .map((target) => normalizeRequirementTarget(target))
                .filter((target) => target.length > 0)
        );
        const normalizedTargets = Array.from(targetSet);

        return EVIDENCE_REQUIREMENT_SOURCE.reduce<EvidenceDocumentRequirement[]>(
            (documents, sourceRow) => {
                if (sourceRow.requiredForAllAssessments) {
                    documents.push({
                        documentName: sourceRow.documentName,
                        requiredForAllAssessments: true,
                        matchedRequirementReferences: []
                    });
                    return documents;
                }

                const matchedRequirementReferences = sourceRow.requirementReferences.filter((reference) =>
                    normalizedTargets.some((target) =>
                        matchesEvidenceRequirementReference(reference, target)
                    )
                );

                if (matchedRequirementReferences.length > 0) {
                    documents.push({
                        documentName: sourceRow.documentName,
                        requiredForAllAssessments: false,
                        matchedRequirementReferences
                    });
                }

                return documents;
            },
            []
        );
    }

    getDocumentsForAssessment(
        formType: unknown,
        descopedSelections: unknown
    ): EvidenceAssessmentInventory {
        const scope = this.getInScopeRequirementTargets(formType, descopedSelections);

        return {
            ...scope,
            documents: this.getDocumentsForRequirementTargets(scope.requirementTargets)
        };
    }
}
